import { useEffect, useState, lazy, Suspense, useRef } from "react"
import { BrowserRouter as Router } from "react-router-dom"
import { useAuthState } from "react-firebase-hooks/auth"
import { auth } from "@/config/firebaseConfig"

import useOrientation from "@/hooks/useOrientation"
import TitleManager from "@/components/Favicon/TitleManager"
import FaviconManager from "@/components//Favicon/FaviconManager"
import KeyboardShortcutsHost from "@/features/AtajosTeclado/KeyboardShortcutsHost"
import { PerformanceProvider } from "@/context/performanceContext"

import { MusicProvider } from "@/context/musicContext"
import { ThemeProvider } from "@/context/ThemeContext"
import { FooterVisibilityProvider } from "@/context/FooterVisibilityContext"
//import Audio from "@/features/footer/audio/audio"
import PlayerDrawer from "@/features/player/PlayerDrawer"
import { AuthProvider } from "@/context/AuthContext"
//import { PlayerDrawerProvider } from "@/features/player/PlayerDrawerContext"

import AppRoutes from "./routes/AppRoutes"
import type { WidgetType } from "./pages/Widgets/types"
import { useIsNativeApp } from "@/hooks/useIsNativeApp"

/* =========================
   LAZY SHELL COMPONENTS
========================= */
const AppSidebarLeft = lazy(() => import("@/app/layouts/sidebarLeft"))
const AppSidebarRight = lazy(() => import("@/app/layouts/sidebarRight"))
const Footer = lazy(() => import("@/features/footer"))
const MobileNavigation = lazy(() => import("@/app/layouts/navMobile"))
const WidgetHost = lazy(() => import("@/app/pages/Widgets/WidgetHost"))

function App() {
  const centerRef = useRef<HTMLDivElement>(null);

  const [activeWidget, setActiveWidget] = useState<WidgetType | null>(null)
  const [widgetsEnabled] = useState(true)
  const [user] = useAuthState(auth)

  const { isPhone, isPhoneLandscape, isLandscape } = useOrientation()

  const isNativeApp = useIsNativeApp()

  const showWidgetHost = isPhone && isPhoneLandscape

  /* =========================
     SMART PRELOAD (BIEN HECHO)
  ========================= */
  useEffect(() => {
    const preloadCritical = () => {
      import("@/app/pages/homePage/home")
      import("@/features/player/PlayerContent")
      import("@/app/pages/radioPage")
      import("@/app/pages/libraryPage")
      import("@/app/pages/buscarPage")
    }

    const preloadSecondary = () => {
      import("@/app/pages/ajustesPage")
      import("@/app/pages/playlistPage/playlist")
      import("@/app/pages/artistPage/ArtistPage")
      import("@/app/pages/albumPage/AlbumPage")
      import("@/app/pages/podcastPage/PodcastPage")
    }

    const run = () => {
      preloadCritical()
      setTimeout(preloadSecondary, 2000)
    }

    if ("requestIdleCallback" in window) {
      ;(window as any).requestIdleCallback(run)
    } else {
      setTimeout(run, 1200)
    }
  }, [])

  return (
    <MusicProvider>
      {/* <Audio /> */}

      <ThemeProvider>
        <FooterVisibilityProvider>
          <PerformanceProvider>

            {/* <PlayerDrawerProvider> */}
              <AuthProvider>
                <Router>
                  <TitleManager />
                  <FaviconManager />

                <KeyboardShortcutsHost />

                  {/* =========================
                      SHELL FIJO (NO SE DESMONTA)
                  ========================= */}
                  <div
                    className={`
                      grid w-full
                      grid-cols-1 gap-2
                      lg:grid-cols-[minmax(240px,18%)_minmax(0,1fr)_minmax(240px,22%)]
                      relative overflow-x-hidden
                      select-none
                      ${isLandscape ? "mx-2 h-[100vh]" : ""}
                    `}
                    style={{
                      background: isLandscape ? "var(--movil-nav)" : undefined,
                    }}
                  >
                  
                    {/* LEFT */}
                    <div className="hidden lg:block min-w-0 overflow-hidden">
                      <Suspense fallback={null}>
                        <AppSidebarLeft />
                      </Suspense>
                    </div>
                  
                    {/* MAIN */}
                    <main ref={centerRef} className="min-w-0 h-full overflow-hidden">
                      <div className="h-full w-full overflow-y-auto overscroll-contain touch-pan-y">
                      <AppRoutes user={user} centerRef={centerRef} />
                      </div>
                    </main>
                        
                    {/* RIGHT */}
                    <div className="hidden lg:block min-w-0 overflow-hidden">
                      <Suspense fallback={null}>
                        <AppSidebarRight />
                      </Suspense>
                    </div>
                  </div>
                        
                  {/* FOOTER (persistente) */}
                  {/* <Suspense fallback={null}>
                    <Footer />
                  </Suspense> */}

                  {!isNativeApp && (
                    <Suspense fallback={null}>
                      <div className="lg:hidden">
                        <Footer />
                      </div>
                    </Suspense>
                  )}
                        
                  {/* MOBILE NAV (persistente) */}
                  {/* <Suspense fallback={null}>
                    <div className="lg:hidden">
                      <MobileNavigation />
                    </div>
                  </Suspense> */}

                  {/* MOBILE NAV — SOLO WEB */}
                  {!isNativeApp && (
                    <Suspense fallback={null}>
                      <div className="lg:hidden">
                        <MobileNavigation />
                      </div>
                    </Suspense>
                  )}
                        
                  {/* WIDGET SYSTEM */}
                  {showWidgetHost && (
                    <Suspense fallback={null}>
                      <WidgetHost
                        enabled={widgetsEnabled}
                        active={activeWidget}
                        setActive={setActiveWidget}
                      />
                    </Suspense>
                  )}

                    <PlayerDrawer />
                
                </Router>
              </AuthProvider>
            {/* </PlayerDrawerProvider> */}
                
          </PerformanceProvider>
        </FooterVisibilityProvider>
      </ThemeProvider>
    </MusicProvider>
  )
}

export default App